import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { GoPlusCircle } from "react-icons/go";
import { FiMinusCircle } from "react-icons/fi";

const API_BASE = "https://demo.zorbastays.com/web-backend/billing";

const emptyRow = () => ({
  particulars: "",
  from: "",
  to: "",
  nights: "",
  amount: "",
  cashback: "",
});

// invoiceid: the cashback invoice to load and edit (falls back to the
// :invoiceid route param so this works whether passed as a prop or rendered
// straight from a route)
// onUpdate: optional callback fired with the server response after a successful save
const CashbackEdit = ({ invoiceid: invoiceidProp, onUpdate }) => {
  const navigate = useNavigate();
  const { invoiceid: invoiceidParam } = useParams();
  const invoiceid = invoiceidProp || invoiceidParam;

  const [invoiceno, setinvoiceno] = useState("");
  const [invoicedate, setinvoicedate] = useState("");
  const [guestname, setguestname] = useState("");
  const [inWords, setInWords] = useState("");
  const [totalAmount, setTotalAmount] = useState("");
  const [rows, setRows] = useState([emptyRow()]);

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formatDateToDisplay = (date) => {
    if (!date) return "";
    const [year, month, day] = date.split("-");
    if (!year || !month || !day) return date;
    return `${day}-${month}-${year}`;
  };

  const handleFocus = (e) => (e.target.type = "date");
  const handleBlur = (e) => (e.target.type = "text");

  // ---------------- Load existing cashback invoice ----------------
  useEffect(() => {
    if (!invoiceid) {
      setIsLoading(false);
      return;
    }

    const fetchInvoice = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        const res = await fetch(`${API_BASE}/cashback-invoice/${invoiceid}`);
        const data = await res.json();

        if (!res.ok || !data.success) {
          setLoadError(data.message || "Failed to load cashback invoice.");
          return;
        }

        const inv = data.invoice;
        setinvoiceno(inv.invoiceno || "");
        setinvoicedate(
          inv.invoicedate ? String(inv.invoicedate).split("T")[0] : "",
        );
        setguestname(inv.guestname || "");
        setInWords(inv.amountinwords || "");
        setTotalAmount(inv.total ?? "");
        setRows(
          inv.items && inv.items.length > 0
            ? inv.items.map((item) => ({
                particulars: item.particulars || "",
                from: item.checkin ? String(item.checkin).split("T")[0] : "",
                to: item.checkout ? String(item.checkout).split("T")[0] : "",
                nights: item.nights ?? "",
                amount: item.amount ?? "",
                cashback: item.cashback ?? "",
              }))
            : [emptyRow()],
        );
      } catch (err) {
        console.error("Error loading cashback invoice:", err);
        setLoadError(
          "Something went wrong while loading the cashback invoice.",
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoice();
  }, [invoiceid]);

  const addRow = () => {
    if (rows.length < 7) {
      setRows([...rows, emptyRow()]);
    }
  };

  const removeRow = (indexToRemove) => {
    setRows(rows.filter((_, index) => index !== indexToRemove));
  };

  const handleInputChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;
    setRows(updatedRows);
  };

  const handleCancel = () => {
    const confirmed = window.confirm("Sure want to discard changes?");
    if (confirmed) {
      navigate("/Bills");
    }
  };

  const handleUpdate = async () => {
    if (!invoiceid) {
      alert("No cashback invoice selected to update.");
      return;
    }
    if (!invoiceno || !invoicedate || !guestname) {
      alert("Invoice No, Invoice Date and Guest Name are required.");
      return;
    }
    if (rows.length === 0) {
      alert("Cashback invoice must contain at least one item.");
      return;
    }

    const payload = {
      invoiceno,
      invoicedate,
      guestname,
      amountinwords: inWords,
      total: totalAmount || 0,
      items: rows.map((row) => ({
        particulars: row.particulars,
        checkin: row.from || null,
        checkout: row.to || null,
        nights: row.nights || 0,
        amount: row.amount || 0,
        cashback: row.cashback || 0,
      })),
    };

    try {
      setIsSubmitting(true);
      const res = await fetch(`${API_BASE}/cashback-invoice/${invoiceid}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        alert(data.message || "Failed to update cashback invoice.");
        return;
      }

      alert("Cashback invoice updated successfully!");
      if (onUpdate) onUpdate(data);
      navigate("/bills");
    } catch (err) {
      console.error("Error updating cashback invoice:", err);
      alert("Something went wrong while updating the cashback invoice.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto p-10 text-center text-gray-600">
        Loading cashback invoice...
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-5xl mx-auto p-10 text-center text-red-600">
        {loadError}
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-10 bg-white text-gray-800 font-sans print:p-0">
      <style>{`
        @media print { @page { margin: 0; } body { margin: 1.6cm; } .no-print { display: none; } }
      `}</style>

      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-4xl font-bold mb-2">Cashback Invoice</h1>
          <br />
          <br />
          <h1 className="nt-seawave text-4xl mb-2 text-black">ZORBA STAYS</h1>
          <p className="text-sm">
            306, 7 - Business Centrum, <br /> Amin Marg, Rajkot, 360001
          </p>
          <p className="text-sm">GSTIN No: 24AADFZ2976K1ZO</p>
        </div>
        <div className="w-24 h-24">
          <img
            src="https://zorbastays.com/android-chrome-512x512.png"
            alt="Zorba Stays Logo"
            className="w-full h-full object-contain"
          />
        </div>
      </div>

      <div className="border-t border-black mb-6"></div>

      {/* Invoice Meta */}
      <div className="flex gap-16 mb-8">
        <div>
          <p className="text-xs font-bold uppercase">Invoice No.</p>
          <input
            type="text"
            value={invoiceno}
            onChange={(e) => setinvoiceno(e.target.value)}
            className="w-20 outline-none border-none bg-transparent focus:ring-0 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
        </div>
        <div>
          <p className="text-xs font-bold uppercase">Invoice Date</p>
          <input
            type="text"
            value={formatDateToDisplay(invoicedate)}
            onChange={(e) => setinvoicedate(e.target.value)}
            className="outline-none border-none bg-transparent focus:ring-0 focus:outline-none"
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder="DD-MM-YYYY"
          />
        </div>
      </div>

      <div className="mb-8">
        <p className="font-bold">Invoice To:-</p>
        <input
          type="text"
          value={guestname}
          onChange={(e) => setguestname(e.target.value)}
          className="w-full text-lg outline-none border-none bg-transparent focus:ring-0 focus:outline-none"
          placeholder="Guest Name"
        />
      </div>

      {/* Table */}
      <table className="w-full border-collapse border-t border-b border-black text-center text-sm">
        <thead>
          <tr className="uppercase text-xs font-bold border-b border-black">
            <th className="py-3  border-black w-[60px]">Sr. No.</th>
            <th className="py-3">Particulars</th>
            <th className="py-3">Period of stay</th>
            <th className="py-3">No. Nights</th>
            <th className="py-3">Bill Amount</th>
            <th className="py-3">Cashback</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index} className="border-b border-black">
              <td className="py-3  border-black">{index + 1}</td>
              <td className="py-3 px-2">
                <input
                  value={row.particulars}
                  onChange={(e) =>
                    handleInputChange(index, "particulars", e.target.value)
                  }
                  className="w-full text-center outline-none"
                />
              </td>
              <td className="py-3 px-2 flex justify-center items-center gap-1">
                <input
                  type="text"
                  value={formatDateToDisplay(row.from)}
                  onChange={(e) =>
                    handleInputChange(index, "from", e.target.value)
                  }
                  className="w-full text-center outline-none border-none bg-transparent focus:ring-0 focus:outline-none"
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  placeholder="DD-MM-YYYY"
                />
                <span>to</span>
                <input
                  type="text"
                  value={formatDateToDisplay(row.to)}
                  onChange={(e) =>
                    handleInputChange(index, "to", e.target.value)
                  }
                  className="w-full text-center outline-none border-none bg-transparent focus:ring-0 focus:outline-none"
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  placeholder="DD-MM-YYYY"
                />
              </td>
              <td className="py-3">
                <input
                  type="number"
                  value={row.nights}
                  onChange={(e) =>
                    handleInputChange(index, "nights", e.target.value)
                  }
                  className="w-full text-center outline-none border-none bg-transparent focus:ring-0 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </td>
              <td className="py-3">
                <input
                  type="number"
                  value={row.amount}
                  onChange={(e) =>
                    handleInputChange(index, "amount", e.target.value)
                  }
                  className="w-full text-center outline-none border-none bg-transparent focus:ring-0 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </td>
              <td className="py-3">
                <input
                  type="number"
                  value={row.cashback}
                  onChange={(e) =>
                    handleInputChange(index, "cashback", e.target.value)
                  }
                  className="w-full text-center outline-none border-none bg-transparent focus:ring-0 focus:outline-none[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </td>
              <td className="py-3 no-print">
                {index >= 1 && (
                  <button
                    onClick={() => removeRow(index)}
                    className="px-2 py-1 hover:scale-110"
                  >
                    <FiMinusCircle size={18} />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="my-2 no-print">
        <button onClick={addRow} disabled={rows.length >= 7}>
          <GoPlusCircle size={22} />
        </button>
      </div>

      <div className="flex justify-between items-start mt-6 px-2">
        <div className="w-1/2">
          <p className="font-bold mb-1">In Words:</p>
          <input
            type="text"
            value={inWords}
            onChange={(e) => setInWords(e.target.value)}
            className="w-full uppercase outline-none border-t-0 border-l-0 border-r-0 border-b border-black focus:ring-0 focus:border-black"
          />
        </div>
        <div className="w-48">
          <div className="flex justify-between py-1 font-bold border-b border-black">
            <span>Total</span>
            <input
              type="number"
              value={totalAmount}
              onChange={(e) => setTotalAmount(e.target.value)}
              className="w-24 text-right outline-none border-none bg-transparent focus:ring-0 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>
        </div>
      </div>

      <div className="mt-20 text-center text-xs border-t border-black pt-8">
        <p>www.zorbastays.com | Contact: +91 63552 31066, +91 98750 28066</p>
      </div>

      <div className="flex justify-center gap-4 mt-8 mb-10 no-print">
        <button
          onClick={handleUpdate}
          disabled={isSubmitting}
          className={`px-6 z-30 py-2 bg-green-900 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-green-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm ${
            isSubmitting ? "opacity-60 cursor-not-allowed" : ""
          }`}
        >
          {isSubmitting ? "Updating..." : "Update"}
        </button>
        <button
          onClick={handleCancel}
          className="px-6 z-30 py-2 bg-gray-800 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-gray-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default CashbackEdit;
